<template>
  <div class="documents-checkout" data-cy="documents-checkout">
    <div class="header-container">
      <h3 class="page-title">
        {{ $t('documentsCheckout.title') }}
      </h3>
      <router-link to="/orders">
        <template v-slot="{ navigate }">
          <button @click="navigate" class="btn-orders">
            <backstage-button icon-right="arrow-right">
              {{ $t('viewAccounting.order_button') }}
            </backstage-button>
          </button>
        </template>
      </router-link>
    </div>
    <div v-if="loading" class="loader"></div>
    <div v-else class="main-container">
      <div v-if="documents.length" class="section-container documents-container">
        <div class="document-item header">
          <span class="document-number" data-cy="document-number">
            {{ $t('documentsCheckout.document_number') }}
          </span>
          <span class="amount">
            {{ $t('documentsCheckout.document_amount') }}
          </span>
        </div>
        <div class="documents-list">
          <div v-for="(document, index) in documents" :key="document.documentNumber" class="document-item">
            <span class="document-number" data-cy="document-number-value">
              {{ document.documentNumber }}
              <button class="btn-download" data-cy="download-button" v-on:click="openDocument(document, index)">
                <backstage-icon v-if="document.loading" class="icon isLoading" name="loading" />
                <backstage-icon v-else class="icon" name="download" />
              </button>
            </span>
            <span class="amount">{{ formatCurrency(document.currencyCode, document.outstandingValue) }}</span>
          </div>
        </div>
        <div class="document-item totals-overview" data-cy="totals-overview">
          <span class="document-number">{{ $t('documentsCheckout.total') }}</span>
          <span class="amount">{{ formatCurrency(documents[0].currencyCode, documentsTotal) }}</span>
        </div>
      </div>
      <div class="section-container payment-form">
        <div v-if="paymentMethods" data-cy="payment-methods-grid">
          <h4 class="sub-title">
            {{ $t('documentsCheckout.select_payment_method') }}
          </h4>
          <div v-for="paymentMethod in paymentMethods" :key="paymentMethod.name">
            <label
              class="radio-options"
              data-cy="payment-method"
              :for="paymentMethod.type"
              v-on:click="selectPaymentMethod(paymentMethod)">
              <input :id="paymentMethod.type" :key="paymentMethod.type" type="radio" name="paymentMethodType" />
              <p>{{ $t(`documentsCheckout[\'${paymentMethod.name}\']`) }}</p>
            </label>
          </div>
          <div id="adyen-payment-container" data-hj-suppress></div>
          <label v-if="!!selectedCardBrandIsInCorrect" class="error">{{
            $t('documentsCheckout.credit_card_not_supported')
          }}</label>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive } from 'vue';
import { useBPLogger } from '@/composables/business-process-logger';
import { type PaymentMethod, PaymentMethodType, UnknownCreditCardBrand } from '@/models/payments.model';
import { PaymentsService } from '@/services/payments.service';
import AdyenCheckout from '@adyen/adyen-web';
import '@adyen/adyen-web/dist/adyen.css';
import { AccountingDocumentsService } from '@/services/accounting-documents.service';
import { AccountingDocument } from '@/models/accounting-document.model';
import { utilsMixins } from '@/mixins/utils.mixins';
import { ValidatorResult } from '@/models/validation-result.model';
import { AppSettings } from '@/constants/app.settings';
import { OrderDocumentService } from '@/services/order-document-service';
import { ordersMixins } from '@/mixins/orders.mixins';
import { paymentsMixins } from '@/mixins/payments.mixins';
import BackstageButton from '@/components/common/BackstageButton.vue';
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import { useLocale } from '@/composables/locale';
import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { useDocumentHelpers } from '@/composables/document-helpers';
import { useFormat } from '../composables/format';
import { COUNTRIES_WITH_PAYMENT_OPTION } from '@/mixins/account.mixins';

export default defineComponent({
  name: 'documents-checkout',
  components: {
    'backstage-button': BackstageButton,
    'backstage-icon': BackstageIcon,
  },
  setup() {
    const { selectedAccountId, selectedAccount } = storeToRefs(useUserStore());
    const { userLocale } = useLocale();
    const userStore = useUserStore();
    const route = useRoute();
    const { openPDF } = useDocumentHelpers();
    const { formatCurrency } = useFormat();
    const { pageViewBPLog } = useBPLogger();

    const documentReferenceIdsArray = (route.params.documentReferenceIds as string)?.split(',');

    return {
      accountId: selectedAccountId,
      documentReferenceIdsArray,
      userLocale,
      selectedAccount,
      userStore,
      openPDF,
      formatCurrency,
      pageViewBPLog,
    };
  },
  watch: {
    accountId(newVal) {
      if (newVal) {
        this.getAccountDocuments();
      }
    },
  },
  data() {
    return {
      loading: true,
      documents: [] as AccountingDocument[],
      documentsTotal: 0,
      paymentsService: new PaymentsService(),
      accountingDocumentsService: new AccountingDocumentsService(),
      orderDocumentsService: new OrderDocumentService(),
      paymentMethods: undefined as PaymentMethod[] | undefined,
      selectedPaymentMethod: undefined as PaymentMethod | undefined,
      adyenClientKey: import.meta.env.VITE_APP_adyenClientKey,
      paymentMethodDetails: undefined,
      adyenCheckout: undefined as any | undefined,
      selectedCardBrandIsInCorrect: undefined as boolean | undefined,
      cardFieldStyles: {
        color: '#000000',
        fontFamily: '"On", Helvetica, sans-serif',
        fontSize: '0.875rem',
        fontWeight: '400',
      },
    };
  },
  async mounted() {
    const [result, documentsResult] = await Promise.all([
      this.paymentsService.getPaymentMethods(this.accountId as string),
      this.accountingDocumentsService.getAll(this.accountId as string), // ToDo we should only get the required documents
    ]);

    this.documents =
      documentsResult.model?.filter(
        (document) =>
          (this.documentReferenceIdsArray.includes(document.referenceId.toString()) &&
            parseFloat(document.outstandingValue as any)) ||
          0 !== 0,
      ) || [];

    this.documentsTotal = this.calculateTotalAmount(this.documents);
    this.loading = false;

    if (result.model) {
      const adyenCheckoutConfiguration = {
        showPayButton: true,
        paymentMethodsResponse: result.model,
        clientKey: this.adyenClientKey,
        locale: this.mapToAdyenLocale(this.userLocale),
        environment: AppSettings.isProduction ? 'live-us' : 'test', // ToDo infer prod env when payment also supported on Europe or Australia
        paymentMethodsConfiguration: {
          card: {
            styles: {
              base: this.cardFieldStyles,
            },
            hasHolderName: true,
            // ToDo : Should be the holder card name required?
            // holderNameRequired: true,
            maskSecurityCode: true,
            enableStoreDetails: true,
            billingAddressRequired: true,
            billingAddressAllowedCountries: COUNTRIES_WITH_PAYMENT_OPTION.map(({ code }) => code),
            showBrandsUnderCardNumber: true,
            positionHolderNameOnTop: true,
            onBrand: (data: any /*CbObjOnBrand*/) => {
              if (data.brand === UnknownCreditCardBrand) {
                this.selectedCardBrandIsInCorrect = true;
              } else {
                this.selectedCardBrandIsInCorrect = false;
              }
            },
          },
          ach: {
            enableStoreDetails: true,
            styles: {
              base: this.cardFieldStyles,
            },
          },
        },
        onChange: (state: any) => {
          if (state.isValid) {
            this.paymentMethodDetails = state.data;
          }
        },
        onSubmit: async (state: any, dropin: any) => {
          if (this.selectedCardBrandIsInCorrect) {
            dropin.setStatus('ready');

            return;
          }

          const response = await this.paymentsService.checkout(this.accountId as string, {
            invoices: this.documents.map((document) => ({
              referenceId: document.referenceId,
              documentNumber: document.documentNumber,
              voucher: document.voucher,
              amount: document.outstandingValue,
            })),
            paymentMethod: state.data.paymentMethod,
            paymentAddress: state.data.billingAddress,
            amount: this.documentsTotal,
          });

          if (response.model?.model?.action) {
            dropin.handleAction(response.model?.model?.action);
          } else {
            this.showSubmissionMessages(response, dropin);
          }
        },
        onAdditionalDetails: async (state: any, dropin: any) => {
          const response = await this.paymentsService.checkoutDetails(this.accountId as string, state.data);

          if (response.model.action) {
            // Drop-in handles the action object from the /payments response
            dropin.handleAction(response.model.action);
          } else {
            // if failed show message
            this.showSubmissionMessages(response, dropin);
          }
        },
      };

      this.paymentMethods = result.model.paymentMethods;
      this.adyenCheckout = await AdyenCheckout(adyenCheckoutConfiguration);
    }

    console.log(this.pageViewBPLog(true, { page_name: 'documents-checkout' }));
  },
  methods: {
    ...utilsMixins,
    ...ordersMixins,
    ...paymentsMixins,
    selectPaymentMethod(paymentMethod: PaymentMethod) {
      this.selectedPaymentMethod = paymentMethod;

      if (paymentMethod.type !== PaymentMethodType.Card) {
        this.selectedCardBrandIsInCorrect = undefined;
      }

      this.adyenCheckout?.create(paymentMethod.type).mount('#adyen-payment-container');
    },
    async openDocument(document: AccountingDocument, index: number) {
      document.loading = true;
      this.documents = reactive(this.documents);
      this.documents[index] = document;

      const validation = await this.orderDocumentsService.get(
        document.salesOrderNumber as string,
        document.documentNumber,
        this.accountId as string,
        this.getDocumentTypeByDocumentNumberForPaymentDocuments(document.documentNumber),
      );

      if (validation.isValid() && validation.model?.base64Body) {
        this.openPDF(validation.model.base64Body, document.documentNumber, document.salesOrderNumber);
      } else {
        this.$snotify.warning(this.$t('documentsCheckout.document_is_not_available'));
      }

      document.loading = false;
      this.documents = reactive(this.documents);
      this.documents[index] = document;
    },
    showSubmissionMessages(response: ValidatorResult<any>, dropin: any) {
      if (response.errors?.length > 0 || response.model.errors?.length > 0) {
        this.$snotify.error(this.$t(response.model.errors[0].error));
        dropin.setStatus('ready');
      } else {
        dropin.setStatus('success');
        this.$snotify.success(this.$t('documentsCheckout.submit_payment_success'));
        this.$router.push('/accounting');
      }
    },
    cancel() {
      this.getAccountDocuments();
    },
    getAccountDocuments() {
      this.$router.push('/accounting');
    },
  },
});
</script>

<style scoped lang="scss">
@mixin flex-row-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

@mixin section-title {
  font-weight: 600;
  font-size: 1rem;
  color: var(--primary-text-color);
  border: none;
  padding: 0;
  margin: 0 0 1rem;
  text-align: left;
}

.documents-checkout {
  @include main-max-with;
  margin: 0 auto;

  .header-container {
    @include flex-row-container;
    box-sizing: border-box;
    padding: 1.5rem 1.25rem;
    height: 7.625rem;

    .page-title {
      font-size: 1.25rem;
      font-weight: 400;
      text-align: left;
    }

    .btn-orders {
      border: none;
      background-color: transparent;
      padding: 0;
    }
  }

  .main-container {
    @include flex-row-container;
    align-items: flex-start;
    flex-wrap: wrap;
    max-width: 70.625rem;
    padding: 0 1.25rem;
    margin: 0 auto 4rem;

    .sub-title {
      @include section-title;
    }

    .section-container {
      width: 49%;
      background: var(--primary-bg-color);
      padding: 1.25rem;
      border-radius: 4px;
      height: fit-content;
    }

    .documents-container {
      .document-item {
        @include flex-row-container;
        height: 3rem;
        padding: 0 1rem;

        &:nth-child(odd) {
          background-color: var(--bg-page-color);
        }

        &.header {
          background-color: var(--primary-bg-color);
          height: fit-content;
          padding: 0;

          .document-number,
          .amount {
            @include section-title;
          }
        }

        .document-number,
        .amount {
          @include flex-row-container;
          font-size: 0.875rem;
          font-weight: 500;
          color: var(primary-text-color);

          .btn-download {
            height: 1.875rem;
            width: 1.875rem;
            cursor: pointer;
            border: none;
            background-color: inherit;

            .icon {
              width: 1.25rem;
              height: 1.25rem;

              &.isLoading {
                width: 30px;
                height: 30px;
                cursor: initial;
              }
            }
          }
        }

        &.totals-overview {
          margin-top: 1rem;
          background-color: var(--bg-remark-color);
          height: 3.5rem;

          .document-number,
          .amount {
            font-size: 1rem;
            font-weight: 700;
          }
        }
      }
    }

    .payment-form {
      .radio-options {
        @include flex-row-container;
        justify-content: flex-start;
        gap: 0.75rem;
        cursor: pointer;
        height: fit-content;
        margin-bottom: 0.8125rem;

        input[type='radio'] {
          cursor: pointer;
          width: 1.375rem;
          height: 1.375rem;
          accent-color: var(--primary-text-color);
          margin: 0;
          padding: 0;
        }

        p {
          font-size: 0.875rem;
          font-weight: 400;
          text-align: center;
          margin: 0;
        }
      }
      label.error {
        color: var(--scarlet-red-color);
        font-size: 0.875rem;
      }
    }
  }

  // we use deep to correctly override adyen styles
  #adyen-payment-container:deep() {
    margin-top: 1.8125rem;
    margin-bottom: 0.3125rem;

    @import '../assets/scss/overrides/adyen.scss';
  }

  @include breakpoint(small) {
    div.main-container {
      margin-bottom: 2rem;
      padding: 0 0.5rem;
      div.section-container {
        width: 100%;
        padding: 0.9375rem;
        margin-bottom: 0.5rem;
      }
    }
  }
}
</style>
